import axios from "axios";
import { notification } from "antd";
import { authenticationService } from "./authenticationService";
import authHeader from "../helpers/authHeader";
import getBaseUrl from "../helpers/getBaseUrl";

const client = axios.create({
  baseURL: getBaseUrl(),
  headers: authHeader(),
});

client.interceptors.request.use(
  (config) => {
    config.baseURL = getBaseUrl();
    const token = localStorage.getItem("token");
    if (token) {
      config.headers = config.headers || {};
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Prevent duplicate alert spam
let lastNotificationTime = 0;
const showNotificationOnce = (type, title, message) => {
  const now = Date.now();
  if (now - lastNotificationTime > 2000) {
    lastNotificationTime = now;
    notification[type]({
      message: title,
      description: message,
      placement: "topRight",
    });
  }
};

// Mutex and Queue for concurrent token refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

client.interceptors.response.use(
  (response) => {
    return response;
  },
  function (error) {
    const originalRequest = error?.config;
    const refreshUrl = "/api/v1/token/refresh/";
    const isSilent = originalRequest?.silentError || false;

    // 1. Avoid infinity loop on refresh
    if (error?.response?.status === 401 && (originalRequest?.url || "").includes(refreshUrl)) {
      processQueue(error, null);
      isRefreshing = false;
      authenticationService.logout();
      showNotificationOnce("warning", "Сессия завершена", "Время действия сессии истекло. Пожалуйста, войдите снова.");
      return Promise.reject(error);
    }

    // 2. Token Refresh attempt on 401
    if (error?.response?.status === 401 && originalRequest && !originalRequest._retry) {
      const refresh = localStorage.getItem("refresh");
      if (!refresh) {
        authenticationService.logout();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers["Authorization"] = "Bearer " + token;
            return client(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      return new Promise(function (resolve, reject) {
        axios
          .post((getBaseUrl() || "") + refreshUrl, { refresh })
          .then((response) => {
            const newAccess = response?.data?.access;
            if (!newAccess) {
              throw new Error("No access token in refresh response");
            }
            authenticationService.updateToken(newAccess);
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers["Authorization"] = "Bearer " + newAccess;
            processQueue(null, newAccess);
            resolve(client(originalRequest));
          })
          .catch((err) => {
            processQueue(err, null);
            authenticationService.logout();
            showNotificationOnce("warning", "Сессия завершена", "Пожалуйста, войдите в систему снова.");
            reject(err);
          })
          .finally(() => {
            isRefreshing = false;
          });
      });
    }

    // 3. Network or connection errors
    if (!error.response && !isSilent) {
      showNotificationOnce(
        "error",
        "Ошибка сети",
        "Не удалось связаться с сервером. Проверьте подключение к сети."
      );
      return Promise.reject(error);
    }

    // 4. Status code specific notifications
    if (error?.response && !isSilent) {
      const status = error.response.status;
      if (status === 403) {
        showNotificationOnce(
          "warning",
          "Доступ ограничен",
          error.response.data?.detail || error.response.data?.message || "У вас недостаточно прав для выполнения этого действия."
        );
      } else if (status >= 500) {
        showNotificationOnce(
          "error",
          "Ошибка сервера",
          error.response.data?.detail || error.response.data?.message || "На сервере произошла внутренняя ошибка. Попробуйте позже."
        );
      }
    }

    return Promise.reject(error);
  }
);

// Auto-check and recover token when tab/window becomes active again after background
if (typeof window !== "undefined" && document.addEventListener) {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      const token = localStorage.getItem("token");
      const refresh = localStorage.getItem("refresh");
      if (token && refresh && !isRefreshing) {
        try {
          const payload = JSON.parse(atob(token.split(".")[1]));
          const exp = payload?.exp ? payload.exp * 1000 : 0;
          // If token expired or expires in less than 60 seconds, proactively refresh
          if (exp && Date.now() >= exp - 60000) {
            isRefreshing = true;
            axios
              .post((getBaseUrl() || "") + "/api/v1/token/refresh/", { refresh })
              .then((res) => {
                if (res?.data?.access) {
                  authenticationService.updateToken(res.data.access);
                  processQueue(null, res.data.access);
                }
              })
              .catch(() => {})
              .finally(() => {
                isRefreshing = false;
              });
          }
        } catch (e) {}
      }
    }
  });
}

export default client;
